import React from 'react';
import { Typography, Box, Divider, Paper, Grid } from '@mui/material';
import { formatSchoolName } from '../../../utils/nameFormatter';
import { TrendingUp, TrendingDown, School as SchoolIcon } from '@mui/icons-material';
import { THRESHOLDS, TREND_COLORS } from '../../../constants/competitiveExplorer';
import { SCHOOL_CATEGORIES } from '../../../utils/schoolCategorization';
import { ENROLLMENT_BASELINE_LABEL } from '../../../constants/ringMembership';
import { 
  formatAcademicValue, 
  getAcademicColorStyle,
  formatCategoryLabel 
} from '../../../utils/academicUtils';

const roundMarketShare = (value) => Math.round(value * 1000) / 10;

const getTrendIcon = (change, schoolCategory) => {
  if (schoolCategory === SCHOOL_CATEGORIES.NEW || change === null) return null;
  const changePercent = change * 100;
  if (Math.abs(changePercent) <= THRESHOLDS.MARKET_SHARE_SIGNIFICANT) return null;
  return changePercent > 0 ? 
    <TrendingUp sx={{ fontSize: 16, color: TREND_COLORS.positive }} /> : 
    <TrendingDown sx={{ fontSize: 16, color: TREND_COLORS.negative }} />;
};

const getChangeColor = (change) => {
  if (change === null) return '#f8f9fa';
  const changePercent = change * 100;
  if (changePercent > THRESHOLDS.MARKET_SHARE_SIGNIFICANT) {
    return '#e8f5e9'; // Light green
  }
  if (changePercent < -THRESHOLDS.MARKET_SHARE_SIGNIFICANT) {
    return '#ffebee'; // Light red
  }
  return '#f8f9fa'; // Default grey
};

const CompXNearbyMapPopup = React.memo(({ 
  school, 
  marketShareData, 
  enrollmentData,
  selectedGrades
}) => {
  const currentMarketShare = Number(marketShareData?.['2024-25'] ?? 0);
  // Null means the school has no comparable baseline. Coercing it to 0 would report the
  // whole of today's share as growth since then.
  const pastMarketShare = marketShareData?.['2019-20'] == null
    ? null
    : Number(marketShareData['2019-20']);
  const changeInMarketShare = pastMarketShare === null
    ? null
    : currentMarketShare - pastMarketShare;
  const schoolCategory = enrollmentData?.category;

  const currentSchoolEnrollment = marketShareData?.enrollments?.['2024-25']?.school;
  const currentTotalEnrollment = marketShareData?.enrollments?.['2024-25']?.total;
  const marketShareRank = marketShareData?.rank;

  return (
    <Box sx={{ width: 300 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {formatSchoolName(school.name)}
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        {school.charter === 'Yes' ? 'Charter' : 'District'} School | {school.grade_range}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Typography variant="body2" color="text.secondary" sx={{ 
        mb: 2,
        fontStyle: 'italic',
        fontSize: '0.75rem'
      }}>
        Note: Enrollment reflects only grades that overlap with selected school ({selectedGrades.join(', ')}).
      </Typography>

      {/* Full-width market share box */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          width: '100%',
          bgcolor: '#f8f9fa',
          border: '1px solid',
          borderColor: 'divider',
          mb: 2
        }}
      >
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold',
          fontSize: '1.75rem',
          mb: 0.5,
          textAlign: 'center'
        }}>
          {roundMarketShare(currentMarketShare)}%
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Market Share
        </Typography>
        {currentSchoolEnrollment && currentTotalEnrollment && (
          <Typography variant="body2" color="text.secondary" align="center">
            {currentSchoolEnrollment.toLocaleString()} of {currentTotalEnrollment.toLocaleString()} students
          </Typography>
        )}
      </Paper>

      {/* Change and Rank side by side */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            bgcolor: getChangeColor(changeInMarketShare),
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, justifyContent: 'center' }}>
            {getTrendIcon(changeInMarketShare, schoolCategory)}
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold',
              fontSize: changeInMarketShare === null ? '1.25rem' : '1.75rem'
            }}>
              {changeInMarketShare === null
                ? 'N/A'
                : `${changeInMarketShare > 0 ? '+' : ''}${roundMarketShare(changeInMarketShare)}%`}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            {changeInMarketShare === null ? `No ${ENROLLMENT_BASELINE_LABEL} baseline` : `Since ${ENROLLMENT_BASELINE_LABEL}`}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            bgcolor: '#f8f9fa',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold',
            fontSize: '1.75rem',
            textAlign: 'center',
            mb: 0.5
          }}>
            {marketShareRank ? `#${marketShareRank}` : '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Market Share Rank
          </Typography>
        </Paper>
      </Box>

      {/* Academic Performance Section - only show if data exists */}
      {enrollmentData?.academics && Object.keys(enrollmentData.academics).length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <SchoolIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Academic Performance
              </Typography>
              {enrollmentData.academics_year && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {enrollmentData.academics_year}
                </Typography>
              )}
            </Box>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 1.5, 
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={1}>
                {Object.entries(enrollmentData.academics).map(([category, metric]) => {
                  const colorStyle = getAcademicColorStyle(metric);
                  return (
                    <Grid item xs={6} key={category}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontSize: '1.1rem',
                            ...colorStyle
                          }}
                        >
                          {formatAcademicValue(metric)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCategoryLabel(category)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
});

CompXNearbyMapPopup.displayName = 'CompXNearbyMapPopup';

export default CompXNearbyMapPopup;